"use client";
import clsx from "clsx";
import {
  type ChangeEvent,
  createContext,
  type FocusEvent,
  type ReactNode,
  type Ref,
  useContext,
  useId,
  useState,
} from "react";
import type { ControlSize } from "../../../lib/FrontendTypes";
import VisuallyHidden from "../../common/VisuallyHidden/VisuallyHidden";
import type { InputProps } from "../InputTypes";
import "./Radio.css";
import "../../common/SegmentedControl/SegmentedControl.css";

interface RadioGroupContextValue {
  size: ControlSize;
  name: string;
  value?: string | null;
  disabled: boolean;
  readOnly: boolean;
  required: boolean;
  error: boolean;
  onValueChange: (value: string) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
}

const RadioGroupContext = createContext<RadioGroupContextValue | undefined>(undefined);

/** The enclosing group's shared selection, name and state, for any kind of radio option. */
export const useRadioGroup = () => useContext(RadioGroupContext);

export interface RadioGroupProps<TValue extends string = string>
  extends Omit<InputProps<TValue>, "error"> {
  /** The group's accessible name, rendered as a visually hidden legend. */
  ariaLabel?: string;
  /** Names the group by a visible element instead of `ariaLabel`. */
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  children: ReactNode;
  required?: boolean;
  readOnly?: boolean;
  error?: string | boolean;
  /** `buttons` lays options out as a segmented control for `RadioButton`s. */
  variant?: "default" | "buttons";
  /** Grid columns for stacked options such as `RadioCard`s, from the small breakpoint up. */
  columns?: 1 | 2 | 3;
  /** Button size for `variant="buttons"`. */
  size?: ControlSize;
  ref?: Ref<HTMLFieldSetElement>;
}

export default function RadioGroup<TValue extends string = string>({
  ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  children,
  name,
  value,
  defaultValue,
  onValueChange,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  readOnly = false,
  required = false,
  error,
  className,
  id,
  ref,
  variant = "default",
  columns = 1,
  size = "md",
}: RadioGroupProps<TValue>) {
  const generatedName = useId();
  const [internalValue, setInternalValue] = useState(defaultValue);
  const invalid = Boolean(error) || ariaInvalid;
  return (
    <fieldset
      ref={ref}
      id={id}
      className={clsx(
        "calmlens-radio-group",
        variant === "buttons" && "calmlens-segmented-control",
        className,
      )}
      data-variant={variant}
      data-columns={variant === "default" ? columns : undefined}
      data-size={variant === "buttons" ? size : undefined}
      data-invalid={invalid || undefined}
      disabled={disabled}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-invalid={invalid || undefined}
    >
      {ariaLabel && !ariaLabelledBy ? (
        <VisuallyHidden as="legend">{ariaLabel}</VisuallyHidden>
      ) : null}
      <RadioGroupContext.Provider
        value={{
          size,
          name: name ?? generatedName,
          value: value === undefined ? internalValue : value,
          disabled,
          readOnly,
          required,
          error: Boolean(invalid),
          onChange: onChange as RadioGroupContextValue["onChange"],
          onBlur,
          onFocus,
          onValueChange: (next) => {
            setInternalValue(next as TValue);
            onValueChange?.(next as TValue);
          },
        }}
      >
        {children}
      </RadioGroupContext.Provider>
    </fieldset>
  );
}
